import {
  normalizePointType,
} from '../dhlPickupPointMap.js'

export { normalizePointType }

export function normalizePickupPoint(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const address = payload.address && typeof payload.address === 'object' ? payload.address : null
  const sap = String(payload.sap ?? payload.code ?? payload.point ?? payload.id ?? '').trim()
  const id = sap !== '' ? sap : String(payload.id ?? '').trim()
  const houseNumber = String(
    payload.streetNo ??
      payload.street_no ??
      payload.houseNo ??
      payload.house_no ??
      payload.houseNumber ??
      payload.house_number ??
      address?.houseNumber ??
      '',
  ).trim()
  const normalized = {
    id,
    sap,
    name: String(payload.name ?? payload.description ?? payload.supplier_name ?? '').trim(),
    description: String(payload.description ?? payload.name ?? '').trim(),
    zip: String(payload.zip ?? payload.postal_code ?? payload.postalCode ?? address?.postalCode ?? '').trim(),
    city: String(payload.city ?? address?.city ?? '').trim(),
    street: String(payload.street ?? address?.street ?? '').trim(),
    streetNo: houseNumber,
    houseNo: houseNumber,
    postnummer: String(payload.postnummer ?? '').trim(),
    type: normalizePointType(payload.type ?? payload.pointType ?? payload.point_type ?? ''),
    supplier: String(payload.supplier?.code ?? payload.supplier ?? '').trim(),
    latitude: String(payload.latitude ?? address?.latitude ?? '').trim(),
    longitude: String(payload.longitude ?? address?.longitude ?? '').trim(),
    openingHours: String(payload.openingHours ?? payload.opening_hours ?? '').trim(),
    isCollectOnDeliveryAvailable: Boolean(payload.isCollectOnDeliveryAvailable ?? payload.services_cod ?? false),
  }

  return Object.entries(normalized).some(([key, value]) => key === 'isCollectOnDeliveryAvailable' ? value : value !== '') ? normalized : null
}

export function pickupPointIdentifier(pickupPoint) {
  if (!pickupPoint) {
    return ''
  }

  return String(pickupPoint.sap || pickupPoint.id || '').trim()
}

export function pickupPointType(pickupPoint) {
  return normalizePointType(pickupPoint?.type ?? pickupPoint?.pointType ?? pickupPoint?.point_type ?? '')
}

export function requiresGermanPostnummer(pickupPoint, country) {
  return pickupPointIdentifier(pickupPoint) !== '' && pickupPointType(pickupPoint) === 'packStation' && String(country ?? '').trim().toUpperCase() === 'DE'
}

export function parsePickupPointFieldValue(value) {
  const rawValue = String(value ?? '').trim()
  if (rawValue === '') {
    return null
  }

  try {
    const parsedValue = JSON.parse(rawValue)
    const pickupPoint = normalizePickupPoint(parsedValue)
    if (pickupPoint) {
      return pickupPoint
    }
  } catch {
    // Keep backward compatibility with legacy field values storing only the point identifier.
  }

  return normalizePickupPoint({
    id: rawValue,
    sap: rawValue,
    name: rawValue,
    description: rawValue,
  })
}

export function serializePickupPointFieldValue(pickupPoint, country) {
  const pointIdentifier = pickupPointIdentifier(pickupPoint)

  return JSON.stringify({
    id: pointIdentifier,
    sap: pointIdentifier,
    name: String(pickupPoint?.name ?? '').trim(),
    zip: String(pickupPoint?.zip ?? '').trim(),
    city: String(pickupPoint?.city ?? '').trim(),
    street: String(pickupPoint?.street ?? '').trim(),
    streetNo: String(pickupPoint?.streetNo ?? '').trim(),
    type: String(pickupPoint?.type ?? '').trim(),
    country: String(country ?? '').trim().toUpperCase(),
    postnummer: String(pickupPoint?.postnummer ?? '').trim(),
  })
}

export function withPickupPointCountry(pickupPoint, country) {
  if (!pickupPoint) {
    return pickupPoint
  }

  return {
    ...pickupPoint,
    country: String(country ?? '').trim().toUpperCase(),
  }
}

export function formatPickupPointTitle(pickupPoint) {
  if (!pickupPoint) {
    return ''
  }

  return pickupPoint.name || pickupPoint.description || pickupPointIdentifier(pickupPoint)
}

export function formatPickupPointAddress(pickupPoint) {
  if (!pickupPoint) {
    return ''
  }

  const streetLine = [pickupPoint.street, pickupPoint.streetNo || pickupPoint.houseNo]
    .filter((part) => String(part ?? '').trim() !== '')
    .join(' ')
  const cityLine = [pickupPoint.zip, pickupPoint.city]
    .filter((part) => String(part ?? '').trim() !== '')
    .join(' ')

  return [streetLine, cityLine]
    .filter((part) => part !== '')
    .join(', ')
}
